import { WDD } from '../core/wdd.js';
import { getUid } from './getUid.js';
import { getOid } from './getOid.js';

/**
 * Loads a commodity pack (relative API path, so the current TLD decides the data)
 * and caches the request in WDD.data.cpacks.
 * @param {number|string} cpack - Commodity pack ID, e.g. 45383
 * @param {Function} [callback] - Called after the request settles
 * @returns {Promise<object>}
 */
export async function loadCommodityPack(cpack, callback) {
    if (!cpack) return;

    cpack = parseInt((cpack + "").trim());

    if (isNaN(cpack) || !/^\d{4,6}$/.test(cpack)) {
        console.error("Given commodity pack is not a valid ID number", cpack);
        return Promise.reject(new Error("Invalid commodity pack ID"));
    }

    // Adding product identifiers to the data
    const addToData = data => {
        // Adding to each data object the UID identifier
        data.forEach(item => {
            item.uid = item.uid || getUid(item);
            item.oid = item.oid || getOid(item);
        });
        return data;
    };

    WDD.data = WDD.data || {};
    WDD.data.cpacks = WDD.data.cpacks || {};
    WDD.data.cpacks[cpack] = WDD.data.cpacks[cpack] || {};

    // Loading the data
    try {
        if (WDD.data.cpacks[cpack].promise) return WDD.data.cpacks[cpack].promise;
        WDD.data.cpacks[cpack].state = "pending";

        const getPromise = async() => {
            const controller = new AbortController();
            const timeout = 5000;
            const timer = setTimeout(() => controller.abort(), timeout);

            try {
                const headers = WDD.data.headers || {};

                const response = await fetch(
                    window.location.hostname === "127.0.0.1" ? `data/${cpack}.json` : `/api/carousels/cd/packs/${cpack}`, {
                    method: 'GET',
                    headers,
                    signal: controller.signal
                });

                clearTimeout(timer);

                if (!response.ok) throw new Error(`Failed to load data for ${cpack}`);

                const data = await response.json();
                WDD.data.cpacks[cpack].data = addToData(data.items);
                WDD.data.cpacks[cpack].state = "fulfilled";

                return data;
            }
            catch (error) {
                if (error.name === 'AbortError') {
                    console.error(`Request for ${cpack} timed out after ${timeout} ms`);
                } else {
                    console.error('Error loading data:', error);
                }
                WDD.data.cpacks[cpack].state = "rejected";
                throw error;
            }
            finally {
                clearTimeout(timer);
                if (typeof callback === "function") callback();
            }
        };

        WDD.data.cpacks[cpack].promise = getPromise();
        return WDD.data.cpacks[cpack].promise;

    }
    catch (error) {
        console.error('Error initializing data promise:', error);
        throw error;
    }
}
